import React, { useEffect, useRef, useState } from 'react';
import { Button, ToastProvider, HireBarProvider, useEscape } from '../../components/ui';
import { campaignAPI, examSetAPI } from '../../services/api';
import usePositionData from './usePositionData';
import { BatchAddCandidatesDialog } from './BatchUI';
import '../../components/ui/ui.css';
import './position.css';

const rowsOf = (res, key) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (key && Array.isArray(d?.[key])) return d[key];
  if (Array.isArray(d?.data)) return d.data;
  return [];
};

/** Step 1 when there is no position yet (e.g. the global Candidates page): choose position + interview. */
function PickTarget({ onPick, onClose }) {
  const boxRef = useRef(null);
  useEscape(onClose, { ref: boxRef });
  const [positions, setPositions] = useState(null);
  const [positionId, setPositionId] = useState('');
  const [interviews, setInterviews] = useState(null);
  const [interviewId, setInterviewId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { boxRef.current?.focus(); }, []);

  useEffect(() => {
    campaignAPI.list(undefined)
      .then((res) => setPositions(rowsOf(res).filter((c) => (c.status || 'active') !== 'archived')))
      .catch(() => { setPositions([]); setError('Could not load positions.'); });
  }, []);

  useEffect(() => {
    setInterviews(null);
    setInterviewId('');
    if (!positionId) return;
    examSetAPI.list(positionId)
      .then((res) => {
        const list = rowsOf(res, 'exam_sets');
        setInterviews(list);
        if (list.length === 1) setInterviewId(list[0].id);
      })
      .catch(() => { setInterviews([]); setError('Could not load interviews for this position.'); });
  }, [positionId]);

  return (
    <>
      <div className="wz-scrim" style={{ zIndex: 1048 }} onClick={onClose} />
      <div className="wz-dialog" style={{ zIndex: 1049 }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div ref={boxRef} tabIndex={-1} className="wz-dialog-box" role="dialog" aria-modal="true" aria-label="Add candidates" style={{ width: 'min(480px, 100%)' }}>
          <div className="wz-dialog-head">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <h2 style={{ fontSize: 18 }}>Add candidates</h2>
              <span className="wz-sub">Choose the position and interview to add them to.</span>
            </div>
            <Button iconOnly icon="x" aria-label="Close" onClick={onClose} />
          </div>
          <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {error && <div className="wz-error" role="alert">{error}</div>}
            <label className="wz-field">
              <span>Position</span>
              <select value={positionId} onChange={(e) => setPositionId(e.target.value)} disabled={positions === null}>
                <option value="">{positions === null ? 'Loading…' : 'Choose a position'}</option>
                {(positions || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.job_code ? ` · ${c.job_code}` : ''}</option>
                ))}
              </select>
            </label>
            <label className="wz-field">
              <span>Interview</span>
              <select value={interviewId} onChange={(e) => setInterviewId(e.target.value)} disabled={!positionId || interviews === null}>
                <option value="">{!positionId ? 'Choose a position first' : interviews === null ? 'Loading…' : interviews.length ? 'Choose an interview' : 'This position has no interviews yet'}</option>
                {(interviews || []).map((es) => <option key={es.id} value={es.id}>{es.name}</option>)}
              </select>
            </label>
          </div>
          <div className="wz-dialog-foot" style={{ justifyContent: 'flex-end' }}>
            <span style={{ display: 'flex', gap: 8 }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button variant="primary" disabled={!positionId || !interviewId} onClick={() => onPick(positionId, interviewId)}>Continue</Button>
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

/** Step 2: load the position like the position page does, then show the normal Add candidates pop-up. */
function LoadedDialog({ campaignId, examSetId, onClose, onAdded }) {
  const data = usePositionData(campaignId);
  const examSet = data.examSets.find((es) => es.id === examSetId);
  if (!data.campaign || data.examSetsLoading || data.candidatesLoading || !examSet) {
    return (
      <>
        <div className="wz-scrim" style={{ zIndex: 1048 }} onClick={onClose} />
        <div className="wz-dialog" style={{ zIndex: 1049 }}>
          <div className="wz-dialog-box" style={{ width: 'min(360px, 100%)', padding: 28 }}>
            <p className="wz-sub" style={{ margin: 0 }}>{data.examSetsError || 'Loading…'}</p>
          </div>
        </div>
      </>
    );
  }
  return (
    <HireBarProvider value={data.hireBar}>
      <BatchAddCandidatesDialog
        mode={(data.batchesByExamSet[examSetId] || []).length ? 'existing' : 'new'}
        campaign={data.campaign}
        examSet={examSet}
        data={data}
        onClose={onClose}
        onDone={async () => { await data.reloadAssignments(); onAdded?.(); }}
      />
    </HireBarProvider>
  );
}

/**
 * The one way to add candidates outside a position page. With campaignId + examSetId it opens the
 * Add candidates pop-up straight away; without them it asks for the position and interview first.
 */
export default function AddCandidatesLauncher({ campaignId = '', examSetId = '', onClose, onAdded }) {
  const [target, setTarget] = useState(campaignId && examSetId ? { campaignId, examSetId } : null);
  return (
    <div className="wz">
      <ToastProvider>
        {target
          ? <LoadedDialog campaignId={target.campaignId} examSetId={target.examSetId} onClose={onClose} onAdded={onAdded} />
          : <PickTarget onClose={onClose} onPick={(c, e) => setTarget({ campaignId: c, examSetId: e })} />}
      </ToastProvider>
    </div>
  );
}
